import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Eye,
  HelpCircle,
  Inbox,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
} from 'lucide-react';
import {
  apiClient,
  type ApiError,
  type CreateMonitoringSubjectInput,
  type MonitoringSource,
  type MonitoringSubject,
  type MonitoringSubjectKind,
  type OfficeCertificateView,
  type Tribunal,
} from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Field, GlassSelect, TextInput } from '@/components/clients/form-primitives';
import { MaskedInput } from '@/components/ui/masked-input';
import { maskCPF, maskCNPJ } from '@/lib/masks';
import { ConfigModal } from './config-modal';
import { describeSource, groupLabel } from './monitoring-source-display';

/**
 * J08.2 — Tela de Monitoramentos.
 *
 * Cadastra um SUJEITO (advogado, PF parte ou PJ parte) e o sistema
 * gera as fontes DJEN automaticamente.
 *
 * Layout do modal alinhado ao padrao ADVBOX (2026-05-11): seletor de tipo,
 * campos especificos por tipo (OAB/UF unificado, CPF mascarado, CNPJ
 * mascarado), select de certificado digital, lista vertical de estados
 * monitorados com "Tribunais Superiores" no topo.
 */

// --- Lista de UFs (na ordem alfabetica solicitada) ----------------------

interface EstadoOption {
  uf: string;
  nome: string;
}

const ESTADOS_BR: ReadonlyArray<EstadoOption> = [
  { uf: 'AC', nome: 'Acre' },
  { uf: 'AL', nome: 'Alagoas' },
  { uf: 'AP', nome: 'Amapá' },
  { uf: 'AM', nome: 'Amazonas' },
  { uf: 'BA', nome: 'Bahia' },
  { uf: 'CE', nome: 'Ceará' },
  { uf: 'DF', nome: 'Distrito Federal' },
  { uf: 'ES', nome: 'Espírito Santo' },
  { uf: 'GO', nome: 'Goiás' },
  { uf: 'MA', nome: 'Maranhão' },
  { uf: 'MT', nome: 'Mato Grosso' },
  { uf: 'MS', nome: 'Mato Grosso do Sul' },
  { uf: 'MG', nome: 'Minas Gerais' },
  { uf: 'PA', nome: 'Pará' },
  { uf: 'PB', nome: 'Paraíba' },
  { uf: 'PR', nome: 'Paraná' },
  { uf: 'PE', nome: 'Pernambuco' },
  { uf: 'PI', nome: 'Piauí' },
  { uf: 'RJ', nome: 'Rio de Janeiro' },
  { uf: 'RN', nome: 'Rio Grande do Norte' },
  { uf: 'RS', nome: 'Rio Grande do Sul' },
  { uf: 'RO', nome: 'Rondônia' },
  { uf: 'RR', nome: 'Roraima' },
  { uf: 'SC', nome: 'Santa Catarina' },
  { uf: 'SP', nome: 'São Paulo' },
  { uf: 'SE', nome: 'Sergipe' },
  { uf: 'TO', nome: 'Tocantins' },
];

// --- Tipos e estado inicial do formulario --------------------------------

interface FormState {
  kind: MonitoringSubjectKind;
  displayName: string;
  // OAB/UF como input unico no formato "123456/UF" para tipo advogado.
  oabCombo: string;
  cpf: string;
  cnpj: string;
  certificateId: string | null;
  monitorTribunaisSuperiores: boolean;
  monitoredStates: string[]; // lista de UFs marcadas
}

const EMPTY: FormState = {
  kind: 'lawyer',
  displayName: '',
  oabCombo: '',
  cpf: '',
  cnpj: '',
  certificateId: null,
  monitorTribunaisSuperiores: false,
  monitoredStates: [],
};

const KIND_LABEL: Record<MonitoringSubjectKind, string> = {
  lawyer: 'Advogado(a)',
  pf: 'Pessoa física',
  pj: 'Pessoa jurídica',
};

// --- Helpers de formatacao / parse ---------------------------------------

function formatCpf(s: string | null): string {
  if (!s) return '';
  const v = s.replace(/\D/g, '');
  if (v.length !== 11) return s;
  return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}`;
}

function formatCnpj(s: string | null): string {
  if (!s) return '';
  const v = s.replace(/\D/g, '');
  if (v.length !== 14) return s;
  return `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5, 8)}/${v.slice(8, 12)}-${v.slice(12)}`;
}


const OAB_REGEX = /^(\d+)\s*\/\s*([A-Z]{2})$/i;

function parseOabCombo(
  raw: string,
): { numero: string; uf: string } | null {
  const m = raw.trim().match(OAB_REGEX);
  if (!m) return null;
  return { numero: m[1], uf: m[2].toUpperCase() };
}

function formatOabCombo(numero: string | null, uf: string | null): string {
  if (!numero || !uf) return '';
  return `${numero}/${uf}`;
}

// --- Componente principal -------------------------------------------------

export function MonitoramentosPage() {
  const [items, setItems] = useState<MonitoringSubject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterActive, setFilterActive] = useState<'true' | 'false' | 'all'>(
    'all',
  );
  const [filterKind, setFilterKind] = useState<MonitoringSubjectKind | 'all'>(
    'all',
  );
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<MonitoringSubject | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [certificates, setCertificates] = useState<OfficeCertificateView[]>([]);

  // Tribunais (J10) — cache infinito; lista é pequena (~64 itens, ~3 KB).
  const { data: tribunals } = useQuery({
    queryKey: ['tribunals', 'all'],
    queryFn: () => apiClient.tribunalsList({}),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const tribunalsMap = useMemo(() => {
    const map = new Map<string, Tribunal>();
    (tribunals ?? []).forEach((t) => map.set(t.code, t));
    return map;
  }, [tribunals]);

  function reload() {
    setLoading(true);
    setError(null);
    apiClient
      .monitoringSubjectsList({
        active: filterActive,
        kind: filterKind === 'lawyer' ? 'pf' : filterKind,
        search: search.trim() || undefined,
      })
      .then((rows) => setItems(rows))
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(reload, [filterActive, filterKind]);

  // Carrega lista de certificados uma vez (usada no select do modal).
  useEffect(() => {
    apiClient
      .officeCertificatesList()
      .then((rows) => setCertificates(rows))
      .catch(() => {
        // Falha silenciosa: o select cai em empty state.
      });
  }, []);

  function openNew() {
    setEditing(null);
    setForm(EMPTY);
    setFormError(null);
    setModalOpen(true);
  }

  function openEdit(item: MonitoringSubject) {
    setEditing(item);
    // O item pode ser PF com OAB (= advogado) ou PF puro. Heuristica: se
    // tem oabNumero+oabUf, tratamos como "lawyer" no UI.
    const inferredKind: MonitoringSubjectKind =
      item.kind === 'pj'
        ? 'pj'
        : item.oabNumero && item.oabUf
          ? 'lawyer'
          : 'pf';
    setForm({
      kind: inferredKind,
      displayName: item.displayName,
      oabCombo: formatOabCombo(item.oabNumero, item.oabUf),
      cpf: item.cpf ?? '',
      cnpj: item.cnpj ?? '',
      certificateId: item.certificateId ?? null,
      monitorTribunaisSuperiores: item.monitorTribunaisSuperiores,
      monitoredStates: item.monitoredStates ?? [],
    });
    setFormError(null);
    setModalOpen(true);
  }

  function toggleState(uf: string) {
    setForm((s) => {
      const has = s.monitoredStates.includes(uf);
      return {
        ...s,
        monitoredStates: has
          ? s.monitoredStates.filter((x) => x !== uf)
          : [...s.monitoredStates, uf],
      };
    });
  }

  async function submit() {
    // Validacoes -----------------------------------------------------------
    if (!form.displayName.trim()) {
      setFormError('Informe o nome');
      return;
    }
    if (form.kind === 'lawyer') {
      const parsed = parseOabCombo(form.oabCombo);
      if (!parsed) {
        setFormError('OAB/UF inválida. Use o formato 123456/UF.');
        return;
      }
    }
    if (form.kind === 'pf') {
      if (form.cpf.length !== 11) {
        setFormError('CPF inválido. Informe 11 dígitos.');
        return;
      }
    }
    if (form.kind === 'pj') {
      if (form.cnpj.length !== 14) {
        setFormError('CNPJ inválido. Informe 14 dígitos.');
        return;
      }
    }
    if (
      form.monitoredStates.length === 0 &&
      !form.monitorTribunaisSuperiores
    ) {
      setFormError('Selecione ao menos 1 estado para monitorar.');
      return;
    }

    setSubmitting(true);
    setFormError(null);
    try {
      // Mapeamento UI -> backend ------------------------------------------
      // 'lawyer' (UI) vira 'pf' (backend), com OAB preenchida.
      const backendKind: 'pf' | 'pj' = form.kind === 'pj' ? 'pj' : 'pf';
      let oabNumero: string | null = null;
      let oabUf: string | null = null;
      if (form.kind === 'lawyer') {
        const parsed = parseOabCombo(form.oabCombo);
        if (parsed) {
          oabNumero = parsed.numero;
          oabUf = parsed.uf;
        }
      }
      const payload: CreateMonitoringSubjectInput = {
        kind: backendKind,
        displayName: form.displayName.trim(),
        cpf: form.kind === 'pf' ? form.cpf || null : null,
        cnpj: form.kind === 'pj' ? form.cnpj || null : null,
        razaoSocial: form.kind === 'pj' ? form.displayName.trim() : null,
        oabNumero,
        oabUf,
        monitorDirectOabOrDoc: true,
        monitorTribunaisSuperiores: form.monitorTribunaisSuperiores,
        monitoredStates: form.monitoredStates,
        certificateId: form.certificateId,
        description: null,
        active: true,
      };
      if (editing) {
        const { kind: _kind, ...rest } = payload;
        void _kind;
        await apiClient.monitoringSubjectsUpdate(editing.id, rest);
      } else {
        await apiClient.monitoringSubjectsCreate(payload);
      }
      setModalOpen(false);
      setFeedback(
        editing
          ? `Monitoramento de "${payload.displayName}" atualizado.`
          : `Monitoramento de "${payload.displayName}" criado.`,
      );
      reload();
    } catch (e) {
      setFormError((e as ApiError).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSync(item: MonitoringSubject) {
    setSyncingId(item.id);
    setFeedback(null);
    try {
      const result = await apiClient.monitoringSubjectsSync(item.id);
      // Conta apenas falhas REAIS de polling. Sources em 'pending' (agregadoras
      // de tribunal/UF que dependem da OAB/nome) e 'no_certificate' (J05
      // aguardando cert A1) NAO contam — sao estados esperados, nao erro.
      const failed = result.sources.filter((s) => s.status === 'failure');
      const detail =
        failed.length > 0
          ? ` ${failed.length} fonte(s) com falha — abra o cadastro para detalhe.`
          : '';
      setFeedback(
        `Sincronização de "${item.displayName}": ${result.importadas} nova(s), ${result.duplicadas} duplicada(s), ${result.vinculadasACase} vinculada(s) a processo.${detail}`,
      );
      reload();
    } catch (e) {
      setFeedback(`Erro ao sincronizar: ${(e as ApiError).message}`);
    } finally {
      setSyncingId(null);
    }
  }

  async function handleDelete(item: MonitoringSubject) {
    if (
      !window.confirm(
        `Remover monitoramento de "${item.displayName}"? As intimações já recebidas continuam no histórico.`,
      )
    )
      return;
    try {
      await apiClient.monitoringSubjectsDelete(item.id);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      setFeedback(`Monitoramento de "${item.displayName}" removido.`);
    } catch (e) {
      window.alert((e as ApiError).message);
    }
  }

  const totalCount = items.length;
  const activeCount = useMemo(
    () => items.filter((i) => i.active).length,
    [items],
  );

  // --- Opcoes do select de certificado -----------------------------------
  const certificateOptions = useMemo(() => {
    return certificates.map((c) => ({
      value: c.id,
      label: `${c.holderName} — ${c.holderDocument}${c.isExpired ? ' (expirado)' : ''}`,
      disabled: c.isExpired,
    }));
  }, [certificates]);

  // Label dinamico do campo "Nome" segundo o tipo.
  const nomeLabel =
    form.kind === 'lawyer'
      ? 'Nome do advogado'
      : form.kind === 'pf'
        ? 'Nome da pessoa'
        : 'Nome da empresa';
  const nomePlaceholder =
    form.kind === 'lawyer'
      ? 'Nome completo do advogado'
      : form.kind === 'pf'
        ? 'Nome completo da pessoa'
        : 'Razão social';

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-[var(--fg)] md:text-2xl">
            Monitoramentos
          </h1>
          <p className="mt-1 text-sm text-[var(--fg-muted)]">
            Cadastre advogados, pessoas físicas ou jurídicas que o escritório
            quer acompanhar nos diários oficiais. Para cada cadastro, o
            sistema gera automaticamente as fontes corretas de busca no DJEN.
          </p>
        </div>
        <Button onClick={openNew} className="md:self-end">
          <Plus className="mr-2 h-4 w-4" />
          Novo monitoramento
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--fg-muted)]" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') reload();
            }}
            placeholder="Buscar por nome, CPF, CNPJ ou OAB..."
            className="h-9 w-72 rounded-md border border-[var(--border)] bg-transparent pl-8 pr-3 text-sm text-[var(--fg)] placeholder:text-[var(--fg-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--fg-muted)]">
          <span className="font-semibold uppercase tracking-wider">Tipo:</span>
          {(['all', 'pf', 'pj'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setFilterKind(k)}
              className={
                filterKind === k
                  ? 'rounded-md bg-[color-mix(in_oklch,var(--accent)_12%,transparent)] px-2.5 py-1 text-[var(--fg)]'
                  : 'rounded-md px-2.5 py-1 text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]'
              }
            >
              {k === 'all' ? 'Todos' : k === 'pf' ? 'PF' : 'PJ'}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--fg-muted)]">
          <span className="font-semibold uppercase tracking-wider">
            Status:
          </span>
          {(['all', 'true', 'false'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilterActive(f)}
              className={
                filterActive === f
                  ? 'rounded-md bg-[color-mix(in_oklch,var(--accent)_12%,transparent)] px-2.5 py-1 text-[var(--fg)]'
                  : 'rounded-md px-2.5 py-1 text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]'
              }
            >
              {f === 'all' ? 'Todos' : f === 'true' ? 'Ativos' : 'Inativos'}
            </button>
          ))}
        </div>
        <div className="ml-auto text-xs text-[var(--fg-muted)]">
          {totalCount} cadastrado(s) · {activeCount} ativo(s)
        </div>
      </div>

      {feedback && (
        <div className="rounded-md border border-[var(--border)] bg-[color-mix(in_oklch,var(--accent)_8%,transparent)] px-4 py-3 text-sm text-[var(--fg)]">
          {feedback}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
        {loading ? (
          <p className="px-4 py-8 text-center text-sm text-[var(--fg-muted)]">
            Carregando...
          </p>
        ) : error ? (
          <p className="px-4 py-8 text-center text-sm text-red-500">{error}</p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
            <Inbox className="h-10 w-10 text-[var(--fg-muted)] opacity-40" />
            <p className="text-sm text-[var(--fg-muted)]">
              Nenhum monitoramento cadastrado ainda.
            </p>
            <Button variant="outline" onClick={openNew}>
              <Plus className="mr-2 h-4 w-4" />
              Cadastrar primeiro monitoramento
            </Button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-left text-xs uppercase tracking-wide text-[var(--fg-muted)]">
              <tr>
                <th className="px-4 py-2 font-medium">Tipo</th>
                <th className="px-4 py-2 font-medium">Identificação</th>
                <th className="px-4 py-2 font-medium">Tribunais</th>
                <th className="px-4 py-2 font-medium">Fontes</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="border-t border-[var(--border)] transition hover:bg-[color-mix(in_oklch,var(--fg)_4%,transparent)]"
                >
                  <td className="px-4 py-2">
                    <span
                      className={
                        item.kind === 'pf'
                          ? 'inline-flex items-center rounded-full bg-blue-500/15 px-2 py-0.5 text-xs font-medium text-blue-500'
                          : 'inline-flex items-center rounded-full bg-purple-500/15 px-2 py-0.5 text-xs font-medium text-purple-500'
                      }
                    >
                      {item.kind === 'pf' ? 'PF' : 'PJ'}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      onClick={() => openEdit(item)}
                      className="text-left font-medium text-[var(--fg)] hover:text-[var(--accent)]"
                    >
                      {item.displayName}
                    </button>
                    <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-[var(--fg-muted)]">
                      {item.cpf && <span>CPF: {formatCpf(item.cpf)}</span>}
                      {item.cnpj && <span>CNPJ: {formatCnpj(item.cnpj)}</span>}
                      {item.oabNumero && item.oabUf && (
                        <span>
                          OAB {item.oabNumero}/{item.oabUf}
                        </span>
                      )}
                      {item.razaoSocial && (
                        <span>Razão social: {item.razaoSocial}</span>
                      )}
                    </div>
                    {item.description && (
                      <p className="mt-1 text-xs text-[var(--fg-muted)] opacity-75">
                        {item.description}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-[var(--fg-muted)]">
                    {item.monitorTribunaisSuperiores ? (
                      <span
                        title="Inclui STJ e STF (será aplicado em breve)"
                        className="inline-flex items-center gap-1"
                      >
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Inclui superiores
                      </span>
                    ) : (
                      <span title="Tribunais padrão (sem STJ/STF)">
                        Padrão
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <SourcesCell
                      sources={item.sources}
                      tribunalsMap={tribunalsMap}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={
                        item.active
                          ? 'inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-500'
                          : 'inline-flex items-center rounded-full bg-zinc-500/15 px-2 py-0.5 text-xs font-medium text-zinc-400'
                      }
                    >
                      {item.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSync(item)}
                        disabled={!item.active || syncingId !== null}
                        title={
                          item.active
                            ? 'Buscar intimações dos últimos 7 dias'
                            : 'Monitoramento inativo'
                        }
                      >
                        <RefreshCw
                          className={
                            syncingId === item.id
                              ? 'h-4 w-4 animate-spin'
                              : 'h-4 w-4'
                          }
                        />
                        <span className="ml-1 hidden md:inline">
                          {syncingId === item.id ? 'Sincronizando' : 'Sincronizar'}
                        </span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(item)}
                        title="Ver detalhes"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item)}
                      >
                        Excluir
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfigModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar termo' : 'Criar novo termo'}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setModalOpen(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting
                ? 'Salvando...'
                : editing
                  ? 'Salvar'
                  : 'Criar novo termo'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {formError && (
            <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">
              {formError}
            </p>
          )}

          {/* Tipo: 3 botoes (advogado / pf / pj) */}
          <Field label="Tipo" required>
            <div className="flex gap-2">
              {(['lawyer', 'pf', 'pj'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  disabled={!!editing}
                  onClick={() => setForm((s) => ({ ...s, kind: k }))}
                  className={
                    form.kind === k
                      ? 'flex-1 rounded-md border-2 border-[var(--accent)] bg-[color-mix(in_oklch,var(--accent)_10%,transparent)] px-3 py-2 text-sm font-medium text-[var(--fg)]'
                      : 'flex-1 rounded-md border border-[var(--border)] px-3 py-2 text-sm text-[var(--fg-muted)] hover:text-[var(--fg)] disabled:cursor-not-allowed disabled:opacity-50'
                  }
                >
                  {KIND_LABEL[k]}
                </button>
              ))}
            </div>
            {editing && (
              <p className="mt-1 text-[11px] text-[var(--fg-muted)] opacity-70">
                Tipo não pode ser alterado após o cadastro.
              </p>
            )}
          </Field>

          {/* Identificador especifico por tipo */}
          {form.kind === 'lawyer' && (
            <Field label="OAB/UF" required>
              <TextInput
                value={form.oabCombo}
                onChange={(e) =>
                  setForm((s) => ({ ...s, oabCombo: e.target.value }))
                }
                placeholder="123456/UF"
                maxLength={15}
              />
            </Field>
          )}
          {form.kind === 'pf' && (
            <Field label="CPF" required>
              <MaskedInput
                value={form.cpf}
                onChange={(clean) => setForm((s) => ({ ...s, cpf: clean }))}
                mask={maskCPF}
                maxDigits={11}
                placeholder="999.999.999-99"
                inputMode="numeric"
              />
            </Field>
          )}
          {form.kind === 'pj' && (
            <Field label="CNPJ" required>
              <MaskedInput
                value={form.cnpj}
                onChange={(clean) => setForm((s) => ({ ...s, cnpj: clean }))}
                mask={maskCNPJ}
                maxDigits={14}
                placeholder="99.999.999/9999-99"
                inputMode="numeric"
              />
            </Field>
          )}

          {/* Nome */}
          <Field label={nomeLabel} required>
            <TextInput
              value={form.displayName}
              onChange={(e) =>
                setForm((s) => ({ ...s, displayName: e.target.value }))
              }
              placeholder={nomePlaceholder}
              maxLength={200}
            />
          </Field>

          {/* Certificado digital */}
          <Field label="Certificado digital">
            <GlassSelect
              value={form.certificateId}
              onChange={(v) =>
                setForm((s) => ({ ...s, certificateId: v || null }))
              }
              options={certificateOptions}
              placeholder="Selecionar certificado digital (A1)"
            />
            <Link
              to="/escritorio/configuracoes/certificado-digital"
              className="mt-1 inline-block text-[11px] text-[var(--accent)] hover:underline"
            >
              Envie o seu certificado digital.
            </Link>
            <p className="mt-1 text-[11px] text-[var(--fg-muted)] opacity-70">
              Vincule o certificado digital do advogado para acessar os
              tribunais e monitorar as intimações eletrônicas.
            </p>
          </Field>

          {/* Estados monitorados */}
          <Field label="Estados monitorados" required>
            <div className="max-h-[300px] overflow-y-auto rounded-md border border-[var(--border)] bg-[color-mix(in_oklch,var(--fg)_3%,transparent)] p-2">
              <label className="flex items-start gap-2 rounded-md px-2 py-1.5 text-sm text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_5%,transparent)]">
                <input
                  type="checkbox"
                  checked={form.monitorTribunaisSuperiores}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      monitorTribunaisSuperiores: e.target.checked,
                    }))
                  }
                  className="mt-0.5 h-4 w-4 rounded border-[var(--border)]"
                />
                <span className="flex flex-1 items-center gap-1.5">
                  Tribunais Superiores
                  <span
                    title="STF, STJ, TST, TSE, STM, CNJ — monitoramento via DJEN/Diários Oficiais"
                    aria-label="STF, STJ, TST, TSE, STM, CNJ — monitoramento via DJEN/Diários Oficiais"
                    className="inline-flex"
                  >
                    <HelpCircle className="h-3.5 w-3.5 text-[var(--fg-muted)]" />
                  </span>
                </span>
              </label>
              {ESTADOS_BR.map((estado) => {
                const checked = form.monitoredStates.includes(estado.uf);
                return (
                  <label
                    key={estado.uf}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_5%,transparent)]"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleState(estado.uf)}
                      className="h-4 w-4 rounded border-[var(--border)]"
                    />
                    <span>{estado.nome}</span>
                  </label>
                );
              })}
            </div>
          </Field>
        </div>
      </ConfigModal>
    </div>
  );
}

// --- Sub-componente: célula "Fontes" com chips agrupados -----------------

interface SourcesCellProps {
  sources: MonitoringSource[];
  tribunalsMap: Map<string, Tribunal>;
}

const PREVIEW_PER_GROUP = 3;

function SourcesCell({ sources, tribunalsMap }: SourcesCellProps) {
  const [expanded, setExpanded] = useState(false);

  // Agrupa por kind preservando ordem natural (djen, tribunal, oab, ...).
  const grouped = useMemo(() => {
    const buckets = new Map<string, MonitoringSource[]>();
    sources.forEach((s) => {
      const arr = buckets.get(s.kind) ?? [];
      arr.push(s);
      buckets.set(s.kind, arr);
    });
    const order: MonitoringSource['kind'][] = [
      'djen',
      'tribunal',
      'oab',
      'cpf',
      'cnpj',
      'nome_parte',
    ];
    return order
      .filter((k) => buckets.has(k))
      .map((k) => ({ kind: k, items: buckets.get(k)! }));
  }, [sources]);

  const totalHidden = useMemo(
    () =>
      grouped.reduce(
        (acc, g) => acc + Math.max(0, g.items.length - PREVIEW_PER_GROUP),
        0,
      ),
    [grouped],
  );

  if (sources.length === 0) {
    return (
      <span className="text-xs text-[var(--fg-muted)] italic">
        Nenhuma fonte ativa
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wider text-[var(--fg-muted)]">
        {grouped.map((g) => (
          <span key={g.kind}>
            {groupLabel(g.kind)} ({g.items.length})
          </span>
        ))}
      </div>

      <div className="flex flex-col gap-1.5">
        {grouped.map((g) => {
          const visible = expanded
            ? g.items
            : g.items.slice(0, PREVIEW_PER_GROUP);
          return (
            <div key={g.kind} className="flex flex-wrap gap-1">
              {visible.map((s) => (
                <SourceChip key={s.id} source={s} tribunalsMap={tribunalsMap} />
              ))}
            </div>
          );
        })}
      </div>

      {totalHidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="self-start text-[11px] font-medium text-[var(--accent)] hover:underline"
        >
          {expanded ? 'Ocultar' : `Ver todas (${sources.length})`}
        </button>
      )}
    </div>
  );
}

interface SourceChipProps {
  source: MonitoringSource;
  tribunalsMap: Map<string, Tribunal>;
}

function SourceChip({ source, tribunalsMap }: SourceChipProps) {
  const d = describeSource(source, tribunalsMap);
  const StatusIcon = d.statusIcon;
  const inactive = !source.active;

  return (
    <span
      title={d.tooltip}
      className={
        inactive
          ? 'inline-flex items-center gap-1 rounded-md border border-dashed border-[var(--border)] px-1.5 py-0.5 text-[10px] font-medium opacity-60 line-through'
          : 'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium'
      }
      style={
        inactive
          ? undefined
          : {
              color: d.color,
              borderColor: `color-mix(in oklch, ${d.color} 35%, transparent)`,
              backgroundColor: `color-mix(in oklch, ${d.color} 10%, transparent)`,
            }
      }
    >
      <StatusIcon
        className="h-3 w-3"
        style={{ color: d.statusColor }}
        aria-label={d.statusLabel}
      />
      <span>{d.label}</span>
      {d.subtitle && (
        <span className="text-[9px] opacity-70">· {d.subtitle}</span>
      )}
    </span>
  );
}
